'use client'

import { useEffect, useState } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'
import Script from 'next/script'
import type { Metric } from 'web-vitals'
import { CookieConsent } from '@/types'

// Analytics configuration - replace with your actual IDs
const ANALYTICS_CONFIG = {
  // Google Analytics 4
  GA4_ID: process.env.NEXT_PUBLIC_GA4_ID || 'G-XXXXXXXXXX',

  // Google Tag Manager (optional - alternative to GA4)
  GTM_ID: process.env.NEXT_PUBLIC_GTM_ID || 'GTM-XXXXXXX',

  // Google Search Console (verification meta tag)
  GSC_VERIFICATION: process.env.NEXT_PUBLIC_GSC_VERIFICATION || '',

  // Microsoft Clarity (optional)
  CLARITY_ID: process.env.NEXT_PUBLIC_CLARITY_ID || '',

  // Hotjar (optional)
  HOTJAR_ID: process.env.NEXT_PUBLIC_HOTJAR_ID || '',
}

// Types for analytics events
export interface AnalyticsEvent {
  name: string
  parameters?: {
    [key: string]: string | number | boolean
  }
}

// Consent-gated Google Analytics 4 implementation
export function GoogleAnalytics({ hasConsent }: { hasConsent: boolean }) {
  const pathname = usePathname()
  const searchParams = useSearchParams()

  useEffect(() => {
    if (
      hasConsent &&
      typeof window !== 'undefined' &&
      window.gtag &&
      ANALYTICS_CONFIG.GA4_ID !== 'G-XXXXXXXXXX'
    ) {
      // Track page view
      window.gtag('config', ANALYTICS_CONFIG.GA4_ID, {
        page_path: pathname + (searchParams?.toString() ? `?${searchParams}` : ''),
        custom_map: {
          custom_parameter_1: 'page_category',
        },
      })
    }
  }, [pathname, searchParams, hasConsent])

  useEffect(() => {
    if (
      !hasConsent ||
      typeof window === 'undefined' ||
      process.env.NODE_ENV === 'development'
    ) {
      return
    }

    const sendWebVitals = ({ name, delta, id }: Metric) => {
      window.gtag?.('event', name, {
        event_category: 'Web Vitals',
        event_label: id,
        value: Math.round(name === 'CLS' ? delta * 1000 : delta),
        non_interaction: true,
      })
    }

    // Loaded on demand: only consenting visitors need it, so it stays out of the initial bundle.
    void import('web-vitals').then(({ onCLS, onFCP, onINP, onLCP, onTTFB }) => {
      onCLS(sendWebVitals)
      onFCP(sendWebVitals)
      onLCP(sendWebVitals)
      onTTFB(sendWebVitals)
      onINP(sendWebVitals)
    })
  }, [hasConsent])

  // Don't render in development, without consent, or if no valid GA4 ID
  if (
    process.env.NODE_ENV === 'development' ||
    !hasConsent ||
    ANALYTICS_CONFIG.GA4_ID === 'G-XXXXXXXXXX'
  ) {
    return null
  }

  return (
    <>
      <Script
        strategy="afterInteractive"
        src={`https://www.googletagmanager.com/gtag/js?id=${ANALYTICS_CONFIG.GA4_ID}`}
      />
      <Script
        id="google-analytics"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${ANALYTICS_CONFIG.GA4_ID}', {
              page_title: document.title,
              page_location: window.location.href,
              custom_map: {
                'custom_parameter_1': 'page_category'
              },
              // Enhanced ecommerce and conversion tracking
              send_page_view: true,
              anonymize_ip: true, // GDPR compliance
              cookie_flags: 'SameSite=Strict;Secure',
              // Core Web Vitals tracking
              'custom_parameter_1': window.location.pathname.split('/')[1] || 'home'
            });

            // Enhanced event tracking for business metrics
            gtag('event', 'page_view', {
              page_title: document.title,
              page_location: window.location.href,
              content_group1: window.location.pathname.split('/')[1] || 'home'
            });
          `,
        }}
      />
    </>
  )
}

// Consent-gated Google Tag Manager implementation (alternative to GA4)
export function GoogleTagManager({ hasConsent }: { hasConsent: boolean }) {
  if (
    process.env.NODE_ENV === 'development' ||
    !hasConsent ||
    ANALYTICS_CONFIG.GTM_ID === 'GTM-XXXXXXX'
  ) {
    return null
  }

  return (
    <>
      <Script
        id="google-tag-manager"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${ANALYTICS_CONFIG.GTM_ID}');
          `,
        }}
      />
      <noscript>
        <iframe
          src={`https://www.googletagmanager.com/ns.html?id=${ANALYTICS_CONFIG.GTM_ID}`}
          height="0"
          width="0"
          style={{ display: 'none', visibility: 'hidden' }}
        />
      </noscript>
    </>
  )
}

// Consent-gated Microsoft Clarity implementation
export function MicrosoftClarity({ hasConsent }: { hasConsent: boolean }) {
  if (process.env.NODE_ENV === 'development' || !hasConsent || !ANALYTICS_CONFIG.CLARITY_ID) {
    return null
  }

  return (
    <Script
      id="microsoft-clarity"
      strategy="afterInteractive"
      dangerouslySetInnerHTML={{
        __html: `
          (function(c,l,a,r,i,t,y){
            c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
            t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
            y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
          })(window, document, "clarity", "script", "${ANALYTICS_CONFIG.CLARITY_ID}");
        `,
      }}
    />
  )
}

// Get current consent status from storage
function getConsentStatus(): CookieConsent | null {
  if (typeof window === 'undefined') return null

  try {
    const stored = localStorage.getItem('m2b-cookie-consent')
    if (stored) {
      const consentData = JSON.parse(stored)
      return consentData.preferences
    }
  } catch (error) {
    console.error('Error reading consent data:', error)
  }

  return null
}

// Consent-aware analytics functions for business events
export function trackEvent(event: AnalyticsEvent) {
  if (typeof window !== 'undefined') {
    const consent = getConsentStatus()

    if (!consent?.analytics) {
      // No analytics consent - don't track
      return
    }

    // Track with Google Analytics 4
    if (window.gtag) {
      window.gtag('event', event.name, event.parameters || {})
    }

    // Track with Google Tag Manager
    if (window.dataLayer) {
      window.dataLayer.push({
        event: event.name,
        ...event.parameters,
      })
    }

    // Track with Microsoft Clarity
    if (window.clarity) {
      window.clarity('event', event.name)
    }
  }
}

// Specific business event tracking functions
export const analytics = {
  // Contact form events
  trackContactFormStart: () =>
    trackEvent({
      name: 'contact_form_start',
      parameters: {
        event_category: 'engagement',
        event_label: 'contact_form',
      },
    }),

  trackContactFormSubmit: (service?: string) =>
    trackEvent({
      name: 'contact_form_submit',
      parameters: {
        event_category: 'conversion',
        event_label: 'contact_form',
        service: service || 'general',
      },
    }),

  // Service page engagement
  trackServiceView: (serviceName: string) =>
    trackEvent({
      name: 'service_view',
      parameters: {
        event_category: 'engagement',
        service_name: serviceName,
      },
    }),

  trackServiceCTAClick: (serviceName: string, ctaType: string) =>
    trackEvent({
      name: 'service_cta_click',
      parameters: {
        event_category: 'conversion',
        service_name: serviceName,
        cta_type: ctaType,
      },
    }),

  // Phone and email clicks
  trackPhoneClick: () =>
    trackEvent({
      name: 'phone_click',
      parameters: {
        event_category: 'conversion',
        event_label: 'phone_call',
      },
    }),

  trackEmailClick: () =>
    trackEvent({
      name: 'email_click',
      parameters: {
        event_category: 'conversion',
        event_label: 'email',
      },
    }),

  // Booking events
  trackBookingClick: (source: string) =>
    trackEvent({
      name: 'booking_click',
      parameters: {
        event_category: 'conversion',
        event_label: 'booking',
        source: source,
      },
    }),

  // Scroll depth tracking
  trackScrollDepth: (percentage: number) =>
    trackEvent({
      name: 'scroll_depth',
      parameters: {
        event_category: 'engagement',
        scroll_depth: percentage,
      },
    }),
}

// Main Analytics component with consent management
export default function Analytics() {
  const [consent, setConsent] = useState<CookieConsent | null>(null)

  useEffect(() => {
    // Check for existing consent on mount
    const storedConsent = getConsentStatus()
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reads localStorage, must run client-only after mount
    setConsent(storedConsent)

    // Listen for consent changes
    const handleConsentChange = (event: CustomEvent<CookieConsent>) => {
      setConsent(event.detail)
    }

    window.addEventListener('cookieConsentChange', handleConsentChange as EventListener)

    return () => {
      window.removeEventListener('cookieConsentChange', handleConsentChange as EventListener)
    }
  }, [])

  // Don't render analytics without consent
  const hasAnalyticsConsent = consent?.analytics ?? false

  return (
    <>
      <GoogleAnalytics hasConsent={hasAnalyticsConsent} />
      <GoogleTagManager hasConsent={hasAnalyticsConsent} />
      <MicrosoftClarity hasConsent={hasAnalyticsConsent} />
    </>
  )
}

// Type declarations for global analytics
declare global {
  interface Window {
    gtag: (...args: any[]) => void
    dataLayer: any[]
    clarity: (...args: any[]) => void
  }
}
